/**
 * i18n Configuration Constants
 * Central configuration for all language-related settings
 */

export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'de', 'hi'] as const;
export const DEFAULT_LANGUAGE = 'en' as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Language metadata for UI display and SEO
export const LANGUAGE_METADATA: Record<SupportedLanguage, {
  name: string;
  nativeName: string;
  htmlLang: string;
  ogLocale: string;
  dir: 'ltr' | 'rtl';
}> = {
  en: {
    name: 'English',
    nativeName: 'English',
    htmlLang: 'en',
    ogLocale: 'en_US',
    dir: 'ltr'
  },
  es: {
    name: 'Spanish',
    nativeName: 'Español',
    htmlLang: 'es',
    ogLocale: 'es_ES',
    dir: 'ltr'
  },
  fr: {
    name: 'French',
    nativeName: 'Français',
    htmlLang: 'fr',
    ogLocale: 'fr_FR',
    dir: 'ltr'
  },
  de: {
    name: 'German',
    nativeName: 'Deutsch',
    htmlLang: 'de',
    ogLocale: 'de_DE',
    dir: 'ltr'
  },
  hi: {
    name: 'Hindi',
    nativeName: 'हिन्दी',
    htmlLang: 'hi',
    ogLocale: 'hi_IN',
    dir: 'ltr'
  }
};

// URL path translations for localized routes
// Note: For fr, de, hi we keep English paths for now. Can be localized later.
export const LOCALIZED_PATHS: Record<string, Record<SupportedLanguage, string>> = {
  '/': {
    en: '/',
    es: '/',
    fr: '/',
    de: '/',
    hi: '/'
  },
  '/tools': {
    en: '/tools',
    es: '/herramientas',
    fr: '/outils',
    de: '/werkzeuge',
    hi: '/tools'
  },
  '/resources': {
    en: '/resources',
    es: '/recursos',
    fr: '/ressources',
    de: '/ressourcen',
    hi: '/resources'
  },
  '/about': {
    en: '/about',
    es: '/acerca',
    fr: '/a-propos',
    de: '/uber-uns',
    hi: '/about'
  },
  '/contact': {
    en: '/contact',
    es: '/contacto',
    fr: '/contact',
    de: '/kontakt',
    hi: '/contact'
  },
  '/pricing': {
    en: '/pricing',
    es: '/precios',
    fr: '/tarifs',
    de: '/preise',
    hi: '/pricing'
  },
  '/profile': {
    en: '/profile',
    es: '/perfil',
    fr: '/profil',
    de: '/profil',
    hi: '/profile'
  },
  '/terms': {
    en: '/terms',
    es: '/terminos',
    fr: '/conditions',
    de: '/bedingungen',
    hi: '/terms'
  },
  '/privacy': {
    en: '/privacy',
    es: '/privacidad',
    fr: '/confidentialite',
    de: '/datenschutz',
    hi: '/privacy'
  },
  '/cookies': {
    en: '/cookies',
    es: '/cookies',
    fr: '/cookies',
    de: '/cookies',
    hi: '/cookies'
  }
};

/**
 * Get the localized path for a given base path and language
 */
export function getLocalizedPath(basePath: string, language: SupportedLanguage): string {
  return LOCALIZED_PATHS[basePath]?.[language] || basePath;
}

/**
 * Get the base path from a localized path
 */
export function getBasePath(localizedPath: string, language: SupportedLanguage): string {
  const entry = Object.entries(LOCALIZED_PATHS).find(
    ([_, translations]) => translations[language] === localizedPath
  );
  return entry ? entry[0] : localizedPath;
}

/**
 * Build full URL with language prefix
 */
export function buildLanguageUrl(path: string, language: SupportedLanguage): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `/${language}${cleanPath}`;
}

/**
 * Extract language from URL path
 */
export function extractLanguageFromPath(pathname: string): SupportedLanguage | null {
  const pathSegments = pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0];
  
  if (firstSegment && SUPPORTED_LANGUAGES.includes(firstSegment as SupportedLanguage)) {
    return firstSegment as SupportedLanguage;
  }
  
  return null;
}

/**
 * Remove language prefix from path
 */
export function removeLanguagePrefix(pathname: string): string {
  const lang = extractLanguageFromPath(pathname);
  if (lang) {
    return pathname.replace(`/${lang}`, '') || '/';
  }
  return pathname;
}

/**
 * Detect preferred language from various sources
 */
export function detectPreferredLanguage(): SupportedLanguage {
  // 1. Check URL path first
  if (typeof window !== 'undefined') {
    const urlLang = extractLanguageFromPath(window.location.pathname);
    if (urlLang) return urlLang;
  }

  // 2. Check localStorage
  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem('finanzly-language');
      if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
        return stored as SupportedLanguage;
      }
    } catch (error) {
      console.warn('Failed to read language from localStorage:', error);
    }
  }

  // 3. Check browser language
  if (typeof navigator !== 'undefined') {
    const browserLang = navigator.language.split('-')[0];
    if (SUPPORTED_LANGUAGES.includes(browserLang as SupportedLanguage)) {
      return browserLang as SupportedLanguage;
    }
  }

  // 4. Fallback to default
  return DEFAULT_LANGUAGE;
}

/**
 * Validate if a language code is supported
 */
export function isValidLanguage(lang: string): lang is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
}
